/**
 * Destination-side tracking snippet builders (GTM mode).
 *
 * Why a standalone module:
 *   • The same snippet HTML must appear in BOTH the Builder UI (where
 *     the operator copies it manually) AND the public Integration
 *     Guide page (DevDoc.tsx, where the destination site's dev team
 *     reads it). Having one source of truth means a fix to the
 *     cookie-bridge applied here flows to both places automatically —
 *     no risk of drift between "operator sees" and "dev sees".
 *
 *   • Each builder is a pure string function so it can be called from
 *     any React component, server-rendered (if ever needed), or
 *     unit-tested without DOM/i18n.
 *
 * GTM-only (since 2026-04):
 *   The previous "Direct Pixel + GA4" mode (raw fbq / gtag inline) was
 *   removed. All operators now ship via GTM:
 *     - eliminates double-fire footguns when GA4 / Pixel are also
 *       installed via GTM container,
 *     - lets HitzLink rotate Pixel IDs / swap BMs without a
 *       destination-side redeploy (auto-sync via Tag Manager API,
 *       see server/services/gtmSync.js),
 *     - leaves a single, audit-friendly surface to debug from.
 *   If a destination site cannot run GTM, an off-platform workaround
 *   is required — the snippet builders here will not produce inline
 *   Pixel / GA4 fallbacks anymore.
 *
 * Snippet shape rationale
 * ───────────────────────
 *   1. HEAD snippet — Cookie bridge + dataLayer init + GTM loader.
 *      The cookie bridge is FIRST so it runs before GTM tags fire,
 *      promoting URL-passed _fbp / _fbc / fbclid to first-party
 *      cookies on the destination domain. Otherwise Pixel tags inside
 *      GTM init with empty match data and Meta can't tie the
 *      conversion back to the original ad click. The bridge ALSO
 *      captures the HitzLink click_id (from the URL param the
 *      operator configured, default `click_id`) into a `_hzcid`
 *      cookie so the registration / deposit snippets can derive a
 *      stable eventID for browser↔CAPI dedup.
 *
 *   2. Registration success — pushes a `hitzlink_complete_register`
 *      Custom Event to dataLayer. The GTM container template
 *      translates that into Pixel CompleteRegistration + GA4 sign_up
 *      (and any other tags the operator added). Browser eventID =
 *      `<click_id>.CompleteRegistration` matches the deterministic id
 *      the server fires via CAPI from the postback handler (see
 *      deriveConversionEventId() server-side).
 *
 *   3. Deposit success — pushes `hitzlink_purchase` with
 *      {value, currency, transaction_id}. The destination's template
 *      engine substitutes YOUR_AMOUNT / YOUR_CURRENCY / YOUR_TXN_ID
 *      with real values at render time. We DON'T hardcode a default
 *      value here — placeholders force the dev team to wire up the
 *      real numbers (otherwise ROAS column would silently be wrong
 *      forever). Browser eventID = `<click_id>.Purchase.<txn_id>` so
 *      each transaction dedupes uniquely against its own CAPI
 *      counterpart.
 */

/** Cookie name the head snippet writes the click_id into, and that the
 *  registration/deposit snippets read back. Short to keep header bytes
 *  low; namespaced so it doesn't collide with anything else on the
 *  destination site's domain. */
const CLICK_ID_COOKIE = '_hzcid';

/** Stable per-browser visitor id cookie. Minted on first contact when
 *  no _hzcid is present, persists 365 days. Used as Meta Pixel
 *  Advanced Matching `external_id` for organic / direct visitors so
 *  the destination site still gets a non-empty identity signal in
 *  every event. The same value is read server-side by the postback
 *  flow — see hashExternalId() in server/services/metaCapi.js. */
const VISITOR_ID_COOKIE = '_hzvid';

/** Window global the head snippet exposes so registration / deposit
 *  scripts on the same page can read the same external_id without
 *  re-doing the cookie lookup. The HEAD snippet's GTM PageView push
 *  already includes this value, so per-event re-init is not required
 *  — but exposing it keeps the contract explicit for any custom
 *  inline tags the destination team might wire up. */
const EXTERNAL_ID_WINDOW_KEY = '__hzExternalId';

/** Fallback when the operator hasn't configured a custom placeholder.
 *  Matches the server-side default in `affiliate.clickIdPlaceholder`. */
const DEFAULT_CLICK_ID_PARAM = 'click_id';

/**
 * dataLayer event names the GTM-mode snippets push, and that the GTM
 * Container Template (gtmContainerTemplate.ts) listens for via Custom
 * Event triggers. Centralised so renaming a hitzlink_* event always
 * touches BOTH sides — drift between snippet and template would
 * silently break tag firing on every single conversion.
 */
export const GTM_DATALAYER_EVENTS = {
  pageView: 'hitzlink_page_view',
  completeRegistration: 'hitzlink_complete_register',
  purchase: 'hitzlink_purchase',
} as const;

/**
 * dataLayer variable names the GTM-mode snippets push, and that the
 * GTM template references via DLV variables. Same single-source-of-
 * truth motivation as GTM_DATALAYER_EVENTS — fix once, no chance of
 * "DLV - hitzlink_event_id" referencing a key the snippet stopped
 * pushing six months ago.
 */
export const GTM_DATALAYER_KEYS = {
  eventId: 'hitzlink_event_id',
  clickId: 'hitzlink_click_id',
  // External_id pushed on every dataLayer event. The GTM template
  // reads this DLV in the Meta Pixel Custom HTML tag's lazy fbq init
  // so Advanced Matching is wired even when the destination site
  // itself never calls fbq directly.
  externalId: 'hitzlink_external_id',
  value: 'hitzlink_value',
  currency: 'hitzlink_currency',
  transactionId: 'hitzlink_transaction_id',
} as const;

// ────────────────────────────────────────────────────────────────────
//  GTM-mode builders
// ────────────────────────────────────────────────────────────────────
//
// Architectural contract with the GTM Container Template:
//   • Custom Event names = GTM_DATALAYER_EVENTS values
//   • Variable keys      = GTM_DATALAYER_KEYS values
//   • Cookie name        = CLICK_ID_COOKIE (used by both snippet AND
//                          a Custom JS variable in the GTM template)
// Renaming any of these requires updating the template's matching
// trigger / variable definitions in lockstep — the constants above
// are the single source of truth.

const GTM_HEAD_HEADER_LINES = [
  '<!-- HitzLink :: GTM-mode tracking — paste in <head> of every page -->',
  '<!-- Make sure to ALSO paste the <noscript> snippet just after <body>. -->',
];

const GTM_REGISTRATION_HEADER = '<!-- HitzLink :: GTM-mode — fire on registration-success page -->';

const GTM_DEPOSIT_HEADER_LINES = [
  '<!-- HitzLink :: GTM-mode — fire on deposit-success page -->',
  '<!-- Replace YOUR_AMOUNT / YOUR_CURRENCY / YOUR_TXN_ID with real values -->',
];

/**
 * Validate a GTM container ID. Google's format is `GTM-XXXXXXX`
 * (uppercase alphanumerics after the dash, length 5-9). We're
 * permissive here — empty input returns false; anything else just has
 * obviously-unsafe characters stripped.
 */
function sanitizeGtmId(gtmId: string): string {
  if (!gtmId) return '';
  // Strip everything except [A-Z0-9-]; clamp to 32 chars to bound
  // any pathological input. The result lands in a JS string literal
  // and a URL — defence in depth so a typo can't break the snippet.
  return gtmId.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 32);
}

/**
 * GTM-mode HEAD snippet:
 *   1. Cookie bridge — captures _fbp / _fbc / fbclid / click_id from
 *      URL into first-party cookies, AND mints a stable `_hzvid`
 *      visitor cookie so Advanced Matching always has an external_id
 *      (improves match quality even for organic / direct traffic).
 *   2. dataLayer init + a synthetic `hitzlink_page_view` push so the
 *      GTM template's Custom Event trigger fires PageView on every
 *      page automatically. We DO NOT use GTM's "All Pages" trigger
 *      for Pixel because it would fire BEFORE our cookie bridge sets
 *      _fbp / _fbc, defeating attribution.
 *   3. GTM container loader (Google's standard async snippet).
 *
 * GA4 + Pixel ARE NOT injected here — they live inside the GTM
 * container the operator imports / auto-syncs separately. That's the
 * whole point of this mode: zero hardcoded vendor IDs in the
 * destination site's HTML.
 */
export function buildGtmHeadSnippet(
  gtmId: string,
  clickIdParam: string = DEFAULT_CLICK_ID_PARAM
): string {
  const lines: string[] = [];
  const safeGtmId = sanitizeGtmId(gtmId);
  const safeParam = String(clickIdParam || DEFAULT_CLICK_ID_PARAM)
    .replace(/[^a-zA-Z0-9_]/g, '')
    .slice(0, 40) || DEFAULT_CLICK_ID_PARAM;

  for (const line of GTM_HEAD_HEADER_LINES) lines.push(line);
  lines.push('');
  lines.push('<script>');
  lines.push('// 1. Cookie bridge — promote attribution params from URL');
  lines.push('//    (passed by HitzLink Link component) to first-party');
  lines.push('//    cookies on THIS domain BEFORE GTM tags fire, so');
  lines.push('//    Pixel tags inside GTM see _fbp / _fbc and the');
  lines.push('//    registration / deposit dataLayer pushes can derive');
  lines.push('//    a stable eventID for browser↔CAPI dedup on Meta.');
  lines.push('(function(){');
  lines.push('  function setCookie(name, value, days) {');
  lines.push('    if (!value) return;');
  lines.push('    var d = new Date();');
  lines.push('    d.setTime(d.getTime() + (days*24*60*60*1000));');
  lines.push('    document.cookie = name + "=" + encodeURIComponent(value) +');
  lines.push('      "; expires=" + d.toUTCString() + "; path=/; SameSite=Lax";');
  lines.push('  }');
  lines.push('  function getParam(n) {');
  lines.push('    try { return new URLSearchParams(location.search).get(n) || ""; }');
  lines.push('    catch(e) { return ""; }');
  lines.push('  }');
  lines.push('  var fbp = getParam("fbp");');
  lines.push('  var fbc = getParam("fbc");');
  lines.push('  var fbclid = getParam("fbclid");');
  lines.push(`  var cid = getParam("${safeParam}");`);
  lines.push('  if (fbp) setCookie("_fbp", fbp, 90);');
  lines.push('  if (fbc) setCookie("_fbc", fbc, 90);');
  lines.push('  if (fbclid && !fbc) setCookie("_fbc", "fb.1." + Date.now() + "." + fbclid, 90);');
  lines.push(`  if (cid) setCookie("${CLICK_ID_COOKIE}", cid, 90);`);
  lines.push('  // Mint stable per-browser visitor id for Advanced Matching');
  lines.push('  // — ensures every event carries a non-empty external_id.');
  lines.push('  function readCookie(n){');
  lines.push("    var x = document.cookie.match(new RegExp('(?:^|;\\\\s*)'+n+'=([^;]*)'));");
  lines.push('    return x ? decodeURIComponent(x[1]) : "";');
  lines.push('  }');
  lines.push(`  var vid = readCookie("${VISITOR_ID_COOKIE}");`);
  lines.push('  if (!vid) {');
  lines.push("    vid = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() :");
  lines.push("      ('v-' + Date.now() + '-' + Math.random().toString(36).slice(2,10));");
  lines.push(`    setCookie("${VISITOR_ID_COOKIE}", vid, 365);`);
  lines.push('  }');
  lines.push(`  window.${EXTERNAL_ID_WINDOW_KEY} = (cid || readCookie("${CLICK_ID_COOKIE}") || vid);`);
  lines.push('})();');
  lines.push('</script>');
  lines.push('');
  lines.push('<script>');
  lines.push('// 2. dataLayer init — must run BEFORE the GTM loader so');
  lines.push('//    early pushes (e.g. our PageView below) are picked up');
  lines.push('//    by tags configured to fire on those Custom Events.');
  lines.push('window.dataLayer = window.dataLayer || [];');
  lines.push('(function(){');
  lines.push(`  var m = document.cookie.match(/(?:^|;\\s*)${CLICK_ID_COOKIE}=([^;]*)/);`);
  lines.push('  var cid = m ? decodeURIComponent(m[1]) : "";');
  lines.push(`  var ext = window.${EXTERNAL_ID_WINDOW_KEY} || "";`);
  lines.push('  // Synthetic PageView — GTM template listens for this');
  lines.push('  // Custom Event so Pixel & GA4 PageView fire AFTER the');
  lines.push('  // cookie bridge above (vs GTM "All Pages" which can');
  lines.push('  // race the bridge and fire empty match data).');
  lines.push('  window.dataLayer.push({');
  lines.push(`    event: '${GTM_DATALAYER_EVENTS.pageView}',`);
  lines.push(`    ${GTM_DATALAYER_KEYS.clickId}: cid,`);
  lines.push(`    ${GTM_DATALAYER_KEYS.externalId}: ext,`);
  lines.push(`    ${GTM_DATALAYER_KEYS.eventId}: cid ? (cid + ".PageView") : ""`);
  lines.push('  });');
  lines.push('})();');
  lines.push('</script>');
  lines.push('');

  if (safeGtmId) {
    lines.push('<!-- Google Tag Manager -->');
    lines.push('<script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({"gtm.start":');
    lines.push("new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],");
    lines.push('j=d.createElement(s),dl=l!="dataLayer"?"&l="+l:"";j.async=true;j.src=');
    lines.push("'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);");
    lines.push(`})(window,document,'script','dataLayer','${safeGtmId}');</script>`);
    lines.push('<!-- End Google Tag Manager -->');
    lines.push('');
    lines.push('<!-- Google Tag Manager (noscript) — paste IMMEDIATELY after <body> -->');
    lines.push(`<noscript><iframe src="https://www.googletagmanager.com/ns.html?id=${safeGtmId}"`);
    lines.push('height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>');
    lines.push('<!-- End Google Tag Manager (noscript) -->');
  } else {
    lines.push('<!-- ⚠️ GTM container ID not configured. Set it in HitzLink Builder');
    lines.push('     (Page settings → Tracking → Google Tag Manager) and reload');
    lines.push('     this Dev Doc to get the GTM loader inserted here. -->');
  }

  return lines.join('\n');
}

/**
 * GTM-mode REGISTRATION snippet — pushes a Custom Event to dataLayer
 * with the deterministic eventID. The GTM container template's
 * Custom Event trigger picks this up and fires Pixel CompleteRegistration
 * + GA4 sign_up (and any other tags the operator added).
 *
 * No vendor SDK calls inline — that's the whole point of GTM mode.
 */
export function buildGtmRegistrationSnippet(): string {
  const lines: string[] = [];
  lines.push(GTM_REGISTRATION_HEADER);
  lines.push('<script>');
  lines.push('(function(){');
  lines.push(`  var m = document.cookie.match(/(?:^|;\\s*)${CLICK_ID_COOKIE}=([^;]*)/);`);
  lines.push('  var cid = m ? decodeURIComponent(m[1]) : "";');
  lines.push(`  var ext = window.${EXTERNAL_ID_WINDOW_KEY} || "";`);
  lines.push('  window.dataLayer = window.dataLayer || [];');
  lines.push('  window.dataLayer.push({');
  lines.push(`    event: '${GTM_DATALAYER_EVENTS.completeRegistration}',`);
  lines.push(`    ${GTM_DATALAYER_KEYS.clickId}: cid,`);
  lines.push(`    ${GTM_DATALAYER_KEYS.externalId}: ext,`);
  lines.push(`    ${GTM_DATALAYER_KEYS.eventId}: cid ? (cid + ".CompleteRegistration") : ""`);
  lines.push('  });');
  lines.push('})();');
  lines.push('</script>');
  return lines.join('\n');
}

/**
 * GTM-mode DEPOSIT snippet. Same shape as registration but carries
 * value / currency / transaction_id payload. Note: Meta wants
 * `value` as a NUMBER, not string — we leave it unquoted in the
 * snippet so the destination's template engine substitutes a numeric
 * literal. If their template defaults strings (Liquid, Jinja, etc.)
 * the operator must wrap with `Number(...)` themselves.
 */
export function buildGtmDepositSnippet(): string {
  const lines: string[] = [];
  for (const line of GTM_DEPOSIT_HEADER_LINES) lines.push(line);
  lines.push('<script>');
  lines.push('(function(){');
  lines.push('  var amount   = YOUR_AMOUNT;        // e.g. 50.00');
  lines.push("  var currency = 'YOUR_CURRENCY';    // e.g. 'USD' or 'THB'");
  lines.push("  var txnId    = 'YOUR_TXN_ID';      // e.g. destination-side transaction id");
  lines.push(`  var m = document.cookie.match(/(?:^|;\\s*)${CLICK_ID_COOKIE}=([^;]*)/);`);
  lines.push('  var cid = m ? decodeURIComponent(m[1]) : "";');
  lines.push(`  var ext = window.${EXTERNAL_ID_WINDOW_KEY} || "";`);
  lines.push("  var eventId = cid ? (cid + '.Purchase' + (txnId ? '.' + txnId : '')) : '';");
  lines.push('  window.dataLayer = window.dataLayer || [];');
  lines.push('  window.dataLayer.push({');
  lines.push(`    event: '${GTM_DATALAYER_EVENTS.purchase}',`);
  lines.push(`    ${GTM_DATALAYER_KEYS.clickId}: cid,`);
  lines.push(`    ${GTM_DATALAYER_KEYS.externalId}: ext,`);
  lines.push(`    ${GTM_DATALAYER_KEYS.eventId}: eventId,`);
  lines.push(`    ${GTM_DATALAYER_KEYS.value}: amount,`);
  lines.push(`    ${GTM_DATALAYER_KEYS.currency}: currency,`);
  lines.push(`    ${GTM_DATALAYER_KEYS.transactionId}: txnId`);
  lines.push('  });');
  lines.push('})();');
  lines.push('</script>');
  return lines.join('\n');
}
